import { db } from '../firebase/config';
import { fetchInstagramWatches, getInstagramWatchByRawId } from './instagramFeed';
import { 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  getDoc
} from 'firebase/firestore';

// Collection reference
const watchesCollectionRef = collection(db, 'watches');

function createdAtMs(data) {
  const c = data.createdAt;
  if (c && typeof c.toMillis === 'function') return c.toMillis();
  if (c && typeof c.seconds === 'number') return c.seconds * 1000;
  return 0;
}

// Get all watches with better error handling
export const getAllWatches = async () => {
  try {
    console.log("getAllWatches: Starting fetch from Firestore...");
    console.log("Collection path:", watchesCollectionRef.path);
    
    const querySnapshot = await getDocs(watchesCollectionRef);
    
    console.log("Query snapshot received, empty?", querySnapshot.empty);
    
    if (querySnapshot.empty) {
      console.log("No watches found in Firestore");
      return [];
    }
    
    const watches = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      console.log("Fetched watch document:", doc.id, data);
      watches.push({
        id: doc.id,
        ...data,
        images: data.images || ['https://via.placeholder.com/800x800?text=No+Image']
      });
    });

    watches.sort((a, b) => createdAtMs(b) - createdAtMs(a));
    
    console.log("Successfully fetched watches:", watches.length);
    return watches;
  } catch (error) {
    console.error("Error getting watches details:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    
    // Try using a fallback approach
    try {
      console.log("Trying fallback approach - simple direct collection fetch");
      const simpleSnapshot = await getDocs(collection(db, 'watches'));
      
      const watches = [];
      simpleSnapshot.forEach(doc => {
        const data = doc.data();
        watches.push({
          id: doc.id,
          ...data,
          images: data.images || ['https://via.placeholder.com/800x800?text=No+Image']
        });
      });
      
      console.log("Fallback approach returned", watches.length, "watches");
      return watches;
    } catch (fallbackError) {
      console.error("Even fallback approach failed:", fallbackError);
      console.error("Fallback error code:", fallbackError.code);
      console.error("Fallback error message:", fallbackError.message);
      return [];
    }
  }
};

// Initialize watches (not needed for Firestore, but kept for compatibility)
export const initializeWatchData = async () => {
  console.log("Initializing watch data...");
  const watches = await getAllWatches();
  console.log("Initialized with", watches.length, "watches");
  return watches;
};

// Add a watch
export const addWatch = async (newWatch) => {
  try {
    console.log("Adding new watch:", newWatch);
    
    // Ensure images array is properly formatted
    if (!newWatch.images || !Array.isArray(newWatch.images) || newWatch.images.length === 0) {
      newWatch.images = ['https://via.placeholder.com/800x800?text=No+Image'];
    }
    
    // Clean up the newWatch object to ensure it can be stored in Firestore
    const watchToAdd = {
      name: newWatch.name || "",
      description: newWatch.description || "",
      price: newWatch.price || "",
      images: newWatch.images,
      createdAt: new Date()
    };
    
    console.log("Cleaned watch data to be added:", watchToAdd);
    
    // Add to Firestore
    const docRef = await addDoc(watchesCollectionRef, watchToAdd);
    console.log("Watch added with ID:", docRef.id);
    
    // Return the added watch with ID
    return {
      id: docRef.id,
      ...watchToAdd
    };
  } catch (error) {
    console.error("Error adding watch details:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    return null;
  }
};

// Get a single watch by ID
export const getWatchById = async (watchId) => {
  try {
    const watchDocRef = doc(db, 'watches', watchId);
    const watchSnap = await getDoc(watchDocRef);
    
    if (watchSnap.exists()) {
      const data = watchSnap.data();
      return { 
        id: watchSnap.id, 
        ...data,
        // Ensure images array exists
        images: data.images || ['https://via.placeholder.com/800x800?text=No+Image']
      };
    } else {
      console.log("No watch found with ID:", watchId);
      return null;
    }
  } catch (error) {
    console.error("Error getting watch:", error);
    return null;
  }
};

// Delete a watch
export const deleteWatch = async (watchId) => {
  try {
    console.log("Deleting watch with ID:", watchId);
    const watchDocRef = doc(db, 'watches', watchId);
    await deleteDoc(watchDocRef);
    console.log("Watch deleted successfully");
    return true;
  } catch (error) {
    console.error("Error deleting watch: ", error);
    return false;
  }
};

/** Firestore + Instagram JSON (ön yüzde kapaklar; tam senkron: npm run sync:instagram) */
export const getAllWatchesWithInstagram = async () => {
  const [firestoreWatches, igWatches] = await Promise.all([
    getAllWatches(),
    fetchInstagramWatches(),
  ]);
  return [...igWatches, ...firestoreWatches];
};

export const resolveWatchById = async (watchId) => {
  if (watchId && String(watchId).startsWith('ig-')) {
    return getInstagramWatchByRawId(String(watchId).slice(3));
  }
  return getWatchById(watchId);
};

// Empty array to maintain compatibility with imports
const watchData = [];
export default watchData; 